import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { canPickCompany } from 'src/app/shared/utilities/company-scope';
import { ICollaboratorReport, IFilterCollaboratorReport } from './data/collaborator-report';
import { CollaboratorReportService, STATUS_FUNCIONARIO_OPTIONS } from './services/collaborator-report.service';

import { CompanyService } from '../companies/services/company.service';
import { ICompany } from '../companies/models/company.interface';

@Component({
  selector: 'app-collaborator-report',
  templateUrl: './collaborator-report.component.html',
  styleUrls: ['./collaborator-report.component.scss']
})
export class CollaboratorReportComponent implements OnInit, OnDestroy {
  ERoleAdminString = ERoleAdminString;
  private readonly destroy$ = new Subject<void>();
  private relatorioSub?: Subscription;

  collaboratorReports: ICollaboratorReport[] = [];
  companyOptions: { id: string; name: string }[] = [{ id: '', name: 'Todas' }];
  canFilterByCompany = false;

  // Filtros da tabela
  orderControl = new UntypedFormControl(EOrdenacao.AlfabeticaAZ);
  cardStatusControl = new UntypedFormControl('');
  companyFilterControl = new UntypedFormControl('Todas');
  statusFuncionarioFilterControl = new UntypedFormControl('');
  searchControl = new UntypedFormControl('');

  get hasActiveFilters(): boolean {
    return (
      !!this.cardStatusControl.value ||
      (!!this.companyFilterControl.value && this.companyFilterControl.value !== 'Todas') ||
      !!this.statusFuncionarioFilterControl.value
    );
  }

  showFilters = false;

  listOrder = [
    { value: EOrdenacao.AlfabeticaAZ, label: 'Alfabética - de A a Z' },
    { value: EOrdenacao.AlfabeticaZA, label: 'Alfabética - de Z a A' }
  ];

  cardStatusOptions = [
    { value: '', label: 'Todos' },
    { value: '1', label: 'Ativo' },
    { value: '0', label: 'Inativo' }
  ];

  statusFuncionarioOptions = STATUS_FUNCIONARIO_OPTIONS;

  dadosPaginacao = {
    ordenacao: EOrdenacao.AlfabeticaAZ,
    paginaSelecionada: 0,
    tamanhoPagina: 10,
    totalPaginas: 0,
    itensTotal: 0
  };

  constructor(
    private readonly collaboratorReportService: CollaboratorReportService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    // Interno e parceiro sem empresa escolhem a empresa; usuário de empresa vê só a dele.
    this.canFilterByCompany = canPickCompany(this.sessionService.getSession());

    this.loadData();
    if (this.canFilterByCompany) {
      this.loadCompanies();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadData(): void {
    this.getCollaboratorReport();

    this.orderControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: EOrdenacao) => {
      this.dadosPaginacao.ordenacao = value;
      this.dadosPaginacao.paginaSelecionada = 0;
      this.getCollaboratorReport();
    });
  }

  private loadCompanies(): void {
    this.companyService
      .listarEmpresasAtivas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const companies = (res.data || []).map((c: ICompany) => ({ id: c.id ?? '', name: c.name }));
          this.companyOptions = [{ id: '', name: 'Todas' }, ...companies];
        }
      });
  }

  public search(): void {
    this.dadosPaginacao.paginaSelecionada = 0;
    this.getCollaboratorReport();
  }

  public getCollaboratorReport(): void {
    const filter = this.buildFilter(this.dadosPaginacao.paginaSelecionada, this.dadosPaginacao.tamanhoPagina);

    // Filtros trocados em sequência: cancela a busca anterior para a resposta antiga não sobrescrever a nova.
    this.relatorioSub?.unsubscribe();
    this.relatorioSub = this.collaboratorReportService
      .getCollaboratorReport(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.collaboratorReports = res.data ?? [];
          const count = res.count ?? 0;
          this.dadosPaginacao.totalPaginas = Math.ceil(count / this.dadosPaginacao.tamanhoPagina);
          this.dadosPaginacao.itensTotal = count;
        },
        error: () => this.toastService.toast('Erro ao carregar relatório. Tente novamente.', EMessageType.Error)
      });
  }

  public cleanFilter(): void {
    this.searchControl.reset();
    this.cardStatusControl.setValue('');
    this.companyFilterControl.setValue('Todas');
    this.statusFuncionarioFilterControl.setValue('');
    this.dadosPaginacao.paginaSelecionada = 0;
    this.getCollaboratorReport();
  }

  public mudouPagina(paginaSelecionada: number): void {
    this.dadosPaginacao.paginaSelecionada = paginaSelecionada;
    this.getCollaboratorReport();
  }

  public mudouTamanhoPagina(itensPaginaSelecionada: number): void {
    this.dadosPaginacao.tamanhoPagina = itensPaginaSelecionada;
    this.dadosPaginacao.paginaSelecionada = 0;
    this.getCollaboratorReport();
  }

  private buildFilter(pageNumber: number, pageSize: number): IFilterCollaboratorReport {
    const filter: IFilterCollaboratorReport = {
      orderBy: Number(this.dadosPaginacao.ordenacao),
      pageNumber,
      pageSize
    };

    if (this.cardStatusControl.value !== '' && this.cardStatusControl.value != null) {
      filter.cardStatus = Number(this.cardStatusControl.value);
    }
    if (this.companyFilterControl.value && this.companyFilterControl.value !== 'Todas') {
      filter.companyName = this.companyFilterControl.value;
    }
    if (this.statusFuncionarioFilterControl.value !== '' && this.statusFuncionarioFilterControl.value != null) {
      filter.statusFuncionario = Number(this.statusFuncionarioFilterControl.value);
    }
    if (this.searchControl.value) filter.searchText = String(this.searchControl.value);

    return filter;
  }
}
